import { Observable } from "rxjs/Observable";
import 'rxjs/add/operator/publishReplay';

import * as error from "../error";
import { enterFunction } from "./listenerWrappers/enterFunction";
import redundancyRemover from "./listenerWrappers/redundancyRemover";
import People from "../people/people";
import { Event } from './Event';
import { ListenerShell } from "./ListenerShell";

var wrappers = { enterFunction, redundancyRemover };

/**
     * Safe lookup a deep path within an object
     * @param path
     * @param obj
     * @returns {*}
     */
var resolvePath = function(path, obj) {
  var _resolvePathSegment = function(pathSegment, obj) {
    if (typeof obj === "undefined" || obj === null || typeof obj !== "object") {
      return null;
    }

    if (!(pathSegment in obj)) {
      return null;
    }

    return obj[pathSegment];
  };

  return (path || []).reduce(function(acc, curr) {
    return _resolvePathSegment(curr, acc);
  }, obj);
};

/**
*
* @constructor
* @param event
* @param dataSourceId
* @param enterFunction - Optional, Allows someone else to intervene in the scheduling the fired listeners.
* Everytime we want to fire a listener, we create a function f, which when called will fire the listener.
* However, instead of calling f, we pass it to this enterFunction. enterFunction can then decide when to fire
* the listeners. It can call f right away, or buffer them to call them periodically.
* By default enterFunction is equivalent to `function (f) {f();}`.
* @constructor
*/
var EventDataSource = function(event: Event, dataSourceId: string, enterFunction) {
  var This = this;
  This._dataSourceId = dataSourceId;
  This._event = event;
  This._listeners = [];
  This._observers = [];
  This._telemetry = event.getTelemetry().clone();
  This._telemetry.context().dsId = dataSourceId;
  This._enterFunction = enterFunction || null;
  This._debug = false;
  This.getTelemetry().debug("created");
};

EventDataSource.prototype.getDataSourceId = function() {
  var This = this;
  return This._dataSourceId;
};

EventDataSource.prototype.getEvent = function() {
  var This = this;
  return This._event;
};

EventDataSource.prototype.getRole = function() {
  var This = this;
  This._event.getRole();
};

EventDataSource.prototype.getTelemetry = function() {
  var This = this;
  if (This._debug) {
    return This._telemetry;
  }

  var unitFunction = function() {};
  return {
    clone: This._telemetry.clone.bind(This._telemetry),
    trace: unitFunction,
    debug: unitFunction,
    info: This._telemetry.info.bind(This._telemetry),
    warn: This._telemetry.warn.bind(This._telemetry),
    error: This._telemetry.error.bind(This._telemetry)
  };
};

EventDataSource.prototype._ensureListenerConnections = function() {
  var This = this;
  This._listeners.forEach(function(l) {
    l.ensureConnection();
  });
};

EventDataSource.prototype._updateValue = function(path, value, callback) {
  var This = this;
  return This.wrapPromise(
    This._event.updateValue(path, value, This.wrapCallback(callback))
  );
};

EventDataSource.prototype._writeValue = function(path, value, callback) {
  var This = this;
  return This.wrapPromise(
    This._event.writeValue(path, value, This.wrapCallback(callback))
  );
};

EventDataSource.prototype._createValue = function(path, value, callback) {
  var This = this;
  return This.wrapPromise(
    This._event.createValue(path, value, This.wrapCallback(callback))
  );
};

EventDataSource.prototype._startValueListener = function(path, listener) {
  var This = this;
  if (typeof listener == "function") {
    var onValueFunction = listener;
    listener = {
      onValue: onValueFunction
    };
  }
  return This._event.startValueListener(path, listener, This);
};

EventDataSource.prototype._startOnceValueListener = function(path, listener) {
  var This = this;
  return This._event.startOnceValueListener(path, listener, This);
};

EventDataSource.prototype._startSetListener = function(path, listener) {
  var This = this;
  return This._event.startSetListener(path, listener, This);
};

EventDataSource.prototype.stopListener = function(l: ListenerShell) {
  var This = this;
  return This._event.stopListener(l, This);
};

EventDataSource.prototype.stopAllListeners = function() {
  var This = this;
  This._observers.forEach(o => o.unsubscribe())
  This._event.stopAllListeners(This);
};

EventDataSource.prototype.wrapListener = function(listener) {
  var This = this;
  return [
    wrappers.enterFunction(This._enterFunction),
    wrappers.redundancyRemover
  ].reduce(function(acc, curr) {
    return curr(acc);
  }, listener);
};

EventDataSource.prototype.wrapCallback = function(callback) {
  const This = this;
  return wrappers.enterFunction(This._enterFunction)(callback || (() => {}));
};

EventDataSource.prototype.wrapPromise = function(p) {
  const This = this;
  const safePromiseLib = This.getEvent().getPromiseLib();
  const defer = safePromiseLib.defer();
  p.then(defer.resolve.bind(defer));
  p.catch(defer.reject.bind(defer));
  return defer.promise;
};

var makePath = (function() {
  var Path = function Path(defArray) {
    var This = this;
    This._definition = defArray;
    This.length = This._definition.length;

    This._props = {};
  };

  Path.prototype.resolve = function(args) {
    var This = this;
    var newDef = This._definition.map(function(arrEntry) {
      if (arrEntry instanceof String || typeof arrEntry === "string") {
        return arrEntry;
      } else if (typeof arrEntry === "number") {
        return args[arrEntry];
      } else if (typeof arrEntry === "function") {
        return arrEntry.apply(null, args);
      }
    });

    return new Path(newDef);
  };

  Path.prototype.asArray = function() {
    var This = this;
    return This._definition.slice();
  };

  Path.prototype.set = function(propName, propValue) {
    var This = this;
    if (typeof propValue == "undefined" || propValue === null) {
      delete This._props[propName];
      return This;
    }

    This._props[propName] = propValue;
    return This;
  };

  Path.prototype.get = function(propName) {
    var This = this;
    if (!(propName in This._props)) {
      return null;
    }

    return This._props[propName];
  };

  return function(...args) {
    var p = new Path(Array.prototype.slice.apply(arguments));
    //Set the most common properties here, and deal with exceptions below
    p.set("valueType", true);
    return p;
  };
})();

var schema = {
  /*not-database*/
  AssetConnection: makePath("assetConnection"),
  EventAclRole: makePath("eventAcl_role"),
  AssetStores: makePath("assetStores"),
  //actually observes last loginless RSVPed person in cookies (not current logged in user)
  CurrentPersonId: makePath("currentPersonId"),
  ServiceApiEventUserProfiles: makePath("serviceApiEventUserProfiles", 0),

  /*info*/
  Info: makePath("database", "info"),
  MessageValue: makePath("database", "info", "message"),
  DateValue: makePath("database", "info", "date"),
  TzidValue: makePath("database", "info", "tzid"),
  OwnerNameValue: makePath("database", "info", "owner"),
  FianceeNameValue: makePath("database", "info", "fiancee"),
  PrimaryLocationValue: makePath("database", "info", "place"),
  PrimaryPhoto: makePath("database", "info", "primaryPhoto"),
  PrimaryPhotoHorizontal: makePath(
    "database",
    "info",
    "primaryPhotoHorizontal"
  ),
  PrimaryPhotoLayoutAlign: makePath(
    "database",
    "info",
    "primaryPhoto",
    "layout",
    "align"
  ),
  Website: makePath("database", "info", "website"),
  MyFont: makePath("database", "info", "myFont"),
  MyStyle: makePath("database", "info", "myStyle"),
  CssOverrides: makePath("database", "info", "cssOverride"),
  PageOrder: makePath("database", "info", "pageOrder"),

  GraphicAccent: makePath("database", "info", "graphicAccent"),     
  Layout: makePath("database", "info", "layout"),     
  LayoutBackgroundType: makePath("database", "info", "layout", "backgroundType"), 
  LayoutTemplate: makePath("database", "info", "layout", "template"), 


  /*info: stringOverrides*/
  StringOverrides: makePath("database", "info", "stringOverrides"),
  WelcomeTitle: makePath("database", "info", "stringOverrides", "welcome"),
  StoryTitle: makePath("database", "info", "stringOverrides", "story"),
  TidbitsTitle: makePath("database", "info", "stringOverrides", "tidbits"),
  RegistryTitle: makePath("database", "info", "stringOverrides", "registry"),
  VipTitle: makePath("database", "info", "stringOverrides", "vip"),
  ScheduleTitle: makePath("database", "info", "stringOverrides", "schedule"),
  FaqTitle: makePath("database", "info", "stringOverrides", "faq"),
  TravelTitle: makePath("database", "info", "stringOverrides", "travel"),
  MomentsTitle: makePath("database", "info", "stringOverrides", "moments"),
  RSVPTitle: makePath("database", "info", "stringOverrides", "RSVP"),

  /*info: theme*/
  BaseColor: makePath("database", "info", "myPalette", "baseColor"),
  BaseText: makePath("database", "info", "myPalette", "baseText"),
  BaseLightText: makePath("database", "info", "myPalette", "baseLightText"),

  /*Channels*/
  ChannelMessages: makePath('database', 'channels', 0, 'messages').set('setType', true),

  /*pages*/
  Faqs: makePath("database", "pages", "data", "faq"),
  FaqsValue: makePath("database", "pages", "data", "faq", "faqs"),
  FaqNote: makePath("database", "pages", "data", "faq", "note"),
  FaqSectionPhoto: makePath(
    "database",
    "pages",
    "data",
    "faq",
    "primaryPhotoHorizontal"
  ),
  Registry: makePath("gateway", "registry"),
  RegistryHasItems: makePath("gateway", "registryHasItems"),
  RegistryPageShouldDisplay: makePath("gateway", "registryPageShouldDisplay"),
  RegistryStoreValues: makePath(
    "database",
    "pages",
    "data",
    "registry",
    "stores"
  ),
  RegistryNote: makePath("database", "pages", "data", "registry", "note"),
  RegistrySectionPhoto: makePath(
    "database",
    "pages",
    "data",
    "registry",
    "primaryPhotoHorizontal"
  ),
  StoryPage: makePath("database", "pages", "data", "story"),
  Story: makePath("database", "pages", "data", "story", "custom", "story"),
  StorySectionPhoto: makePath(
    "database",
    "pages",
    "data",
    "story",
    "primaryPhotoHorizontal"
  ),
  Tidbits: makePath("database", "pages", "data", "story", "qandas"),
  Vip: makePath("database", "pages", "data", "vip"),
  Vips: makePath("database", "pages", "data", "vip", "vips"),
  VipSectionPhoto: makePath(
    "database",
    "pages",
    "data",
    "vip",
    "primaryPhotoHorizontal"
  ),
  Schedule: makePath("database", "pages", "data", "schedule"),
  ScheduleCalendar: makePath(
    "database",
    "pages",
    "data",
    "schedule",
    "calendar"
  ),
  ScheduleSectionPhoto: makePath(
    "database",
    "pages",
    "data",
    "schedule",
    "primaryPhotoHorizontal"
  ),
  Travel: makePath("database", "pages", "data", "travel"),
  TravelNotes: makePath("database", "pages", "data", "travel", "notes"),
  TravelSleep: makePath("database", "pages", "data", "travel", "sleep"),
  TravelTransportation: makePath(
    "database",
    "pages",
    "data",
    "travel",
    "transportation"
  ),
  TravelLists: makePath("database", "pages", "data", "travel", "lists"),
  TravelSectionPhoto: makePath(
    "database",
    "pages",
    "data",
    "travel",
    "primaryPhotoHorizontal"
  ),
  PageVisibilities: makePath("database", "pages", "visibility"),
  PageConfig: makePath("database", "pages", "config"),

  /*security*/
  SecuritySettings: makePath("database", "security"),
  PageSecuritySettings: makePath("database", "security", "pages"),
  ChannelSecuritySettings: makePath("database", "security", "channels"),
  UserSecuritySettings: makePath("database", "security", "users"),
  RsvpStrictNameMatch: makePath(
    "database",
    "security",
    "users",
    "rsvpStrictNameMatch"
  ),
  InfoSecuritySettings: makePath("database", "security", "info"),

  /*rsvp*/
  Rsvp: makePath("database", "rsvp"),
  RsvpQuestionOverrides: makePath("database", "rsvp", "overrides"),
  RsvpQuestionOverride: makePath("database", "rsvp", "overrides", 0),
  RsvpCustomQuestions: makePath("database", "rsvp", "customQuestions").set(
    "setType",
    true
  ),
  RsvpCustomQuestion: makePath("database", "rsvp", "customQuestions", 0),
  RsvpCustomQuestionProperty: makePath(
    "database",
    "rsvp",
    "customQuestions",
    0,
    1
  ),
  RsvpCustomQuestionChoice: makePath(
    "database",
    "rsvp",
    "customQuestions",
    0,
    "choices",
    1
  ),
  RsvpCustomQuestionDependencies: makePath(
    "database",
    "rsvp",
    "customQuestions",
    0,
    "dependencies"
  ).set("setType", true),
  RsvpCustomQuestionDependency: makePath(
    "database",
    "rsvp",
    "customQuestions",
    0,
    "dependencies",
    1
  ),
  RsvpQuestionSortKey: makePath("database", "rsvp", "sortKeys", 0),
  RsvpHouseholds: makePath("database", "rsvp", "households").set(
    "setType",
    true
  ),
  RsvpHouseholdProperty: makePath("database", "rsvp", "households", 0, 1),

  /*users*/
  UserProperty: makePath("database", "users", 0, 1),
  UserProfile: makePath("database", "users", 0, "profile"),
  UserProfileName: makePath("database", "users", 0, "profile", "name"),
  UserProfileDisplayName: makePath(
    "database",
    "users",
    0,
    "profile",
    "displayName"
  ),
  UserProfileAvatar: makePath("database", "users", 0, "profile", "avatar"),
  UserProfileProperty: makePath("database", "users", 0, "profile", 1).set(
    "timestamp",
    true
  ),
  Users: makePath("database", "users").set("setType", true),
  UserPreferences: makePath("database", "users", 0, "preferences"),
  UserPreferencesGuestListColumns: makePath(
    "database",
    "users",
    0,
    "preferences",
    "guestList",
    "columns"
  ),

  /*invitees*/
  Invitees: makePath("database", "invites", "invitees").set("setType", true),
  InviteeProperty: makePath("database", "invites", "invitees", 0, 1).set(
    "timestamp",
    true
  ),

  /*drafts*/
  Draft: makePath("database", "invites", "drafts", 0),
  CurrentDraft: makePath("database", "invites", "currentDrafts", 0),
  Drafts: makePath("database", "invites", "drafts").set("setType", true)
};

Object.keys(schema).forEach(function(key) {
  var path = schema[key];
  var timestampOpt = path.get("timestamp");

  if (!!path.get("valueType")) {
    EventDataSource.prototype["observe" + key] = function() {
      var This = this;

      //The last parameter is the listener
      var listener = Array.prototype.slice.call(arguments, -1)[0];

      //Use all other parameters to resolve the path
      var args = Array.prototype.slice.call(arguments, 0, -1);
      var resolvedArray = path.resolve(args).asArray();
      return This._startValueListener(resolvedArray, listener);
    };

    EventDataSource.prototype["observe" + key + "$"] = function() {
      var This = this;

      // Use all other parameters to resolve the path
      var args = Array.prototype.slice.call(arguments);
      var resolvedArray = path.resolve(args).asArray();

      return new Observable(observer => {

        const listener = {
          onValue: val => observer.next(val),
          onError: err => (err.code !== "PERMISSION_DENIED") ? observer.error(err) : null
        };

        let l: ListenerShell = This._startValueListener(resolvedArray, listener);

        This._observers.push(observer)
        observer.add(() => {
          This.stopListener(l)
          This._observers.splice(This._observers.indexOf(observer), 1);
        })
      }).publishReplay().refCount()
    };

    EventDataSource.prototype["observeOnce" + key] = function() {
      var This = this;

      //The last parameter is the listener
      var listener = Array.prototype.slice.call(arguments, -1)[0];

      //Use all other parameters to resolve the path
      var args = Array.prototype.slice.call(arguments, 0, -1);
      var resolvedArray = path.resolve(args).asArray();

      return This._startOnceValueListener(resolvedArray, listener);
    };

    EventDataSource.prototype["write" + key] = function() {
      var This = this;

      //The last parameter is the listener
      var callback = Array.prototype.slice.call(arguments, -1)[0];

      //The value to write is the second last argument
      var value = Array.prototype.slice.call(arguments, -2, -1)[0];

      //Use all other parameters to resolve the path
      var args = Array.prototype.slice.call(arguments, 0, -2);
      var resolvedArray = path.resolve(args).asArray();

      var key = resolvedArray.slice(-1)[0];
      var updateValue = {};

      if (key instanceof Array) {
        updateValue[key.join("/")] = value;
      } else {
        updateValue[key] = value;
      }

      if (!!timestampOpt) {
        var timestampKey = key instanceof Array ? key[0] : key;
        updateValue[timestampKey + "-timestamp"] = Date.now();
      }

      return This._updateValue(
        resolvedArray.slice(0, -1),
        updateValue,
        callback
      );
    };
  }

  if (!!path.get("setType")) {
    EventDataSource.prototype["observe" + key + "AsSet"] = function() {
      var This = this;

      //The last parameter is the listener
      var listener = Array.prototype.slice.call(arguments, -1)[0];

      //Use all other parameters to resolve the path
      var args = Array.prototype.slice.call(arguments, 0, -1);
      var resolvedArray = path.resolve(args).asArray();

      return This._startSetListener(resolvedArray, listener);
    };

    EventDataSource.prototype["remove" + key + "Child"] = function() {
      var This = this;

      //The last parameter is the listener
      var callback = Array.prototype.slice.call(arguments, -1)[0];

      var key = Array.prototype.slice.call(arguments, -2, -1)[0];

      //Use all other parameters to resolve the path
      var args = Array.prototype.slice.call(arguments, 0, -2);
      var resolvedArray = path.resolve(args).asArray();
      resolvedArray.push(key);

      return This._writeValue(resolvedArray, null, callback);
    };

    EventDataSource.prototype["create" + key + "Child"] = function() {
      var This = this;
      //The last parameter is the listener
      var callback = Array.prototype.slice.call(arguments, -1)[0];

      //The value to write is the second last argument
      var value = Array.prototype.slice.call(arguments, -2, -1)[0];

      //Use all other parameters to resolve the path
      var args = Array.prototype.slice.call(arguments, 0, -2);
      var resolvedArray = path.resolve(args).asArray();

      return This._createValue(resolvedArray, value, callback);
    };
  }
});

const graphSchema = {
  WelcomeVideo: `
    welcomeVideo {
      asset {
        type:__typename
        ... on ExternalAsset {
          id,
          url,
          viewerUrl,
          mimeType,
          thumbnailUrl,
          title
        }
      },
      caption
    }
  `
}

Object.keys(graphSchema).forEach(key => (
  EventDataSource.prototype[`query${key}`] = function() {
    const eventId = this.getEvent().getEventId();
    const api = this.getEvent().getBestServiceApi();
    return new Observable(observer => {

      this._observers.push(observer)
      observer.add(() => {
        this._observers.splice(this._observers.indexOf(observer), 1);
      })

      api.queryGraphQLWithErrors(`
        query {
          event(eventId: "${eventId}") {
            ${graphSchema[key]}
          }
        }
      `)
      .then(data => observer.next(data.event))
      .catch(err => observer.error(err));
    }).publishReplay().refCount();
  }
));

EventDataSource.prototype.getPeople = function(listener) {
  var This = this;

  var ignoreSource = function(target) {
    return function(source, key, obj) {
      return target(key, obj);
    };
  };

  var fakeSource = function(target) {
    return function(key, obj) {
      return target(null, key, obj);
    };
  };

  var offsetAndForward = function(path, forwardFn) {
    return fakeSource(function(source, key, obj) {
      return forwardFn(source, key, resolvePath(path, obj));
    });
  };


  var people = new People({
      onChildAdded: ignoreSource(listener.onChildAdded),
      onChildChanged: ignoreSource(listener.onChildChanged),
      onChildRemoved: ignoreSource(listener.onChildRemoved)
  }, {
      inviteeWriter: {
          addChild: This.createInviteesChild.bind(This),
          changeChild: This.writeInviteeProperty.bind(This),
          removeChild: This.removeInviteesChild.bind(This)
      },
      userProfileWriter: {
          addChild: console.log.bind(console, "userProfileWriter-add"),
          changeChild: function (userId, path, value, callback) {
              if (path.length >= 1 && path[0] == "profile") {
                  var profilePath = path.slice(1);
                  return This.writeUserProfileProperty(userId, profilePath, value, callback);
              }
              return This.writeUserProperty(userId, path, value, callback);
          },
          removeChild: This.removeUsersChild.bind(This)
      }
  });


  var profileListener = people.getProfileListener();
  This.observeUsersAsSet({
      onConnected: listener.onConnected,
      onChildAdded: offsetAndForward([], profileListener.onChildAdded.bind(profileListener)),
      onChildChanged: offsetAndForward([], profileListener.onChildChanged.bind(profileListener)),
      onChildRemoved: offsetAndForward([], profileListener.onChildRemoved.bind(profileListener))
  });

  var inviteeListener = people.getInviteeListener();
  This.observeInviteesAsSet({
      onChildAdded: offsetAndForward([], inviteeListener.onChildAdded.bind(inviteeListener)),
      onChildChanged: offsetAndForward([], inviteeListener.onChildChanged.bind(inviteeListener)),
      onChildRemoved: offsetAndForward([], inviteeListener.onChildRemoved.bind(inviteeListener))
  });

  return people;
};

EventDataSource.prototype.getCurrentPersonSpotlight = function(listener) {
  var This = this;

  /**
  * Given a data source, This class allows you to so the following
  *   1. Allows you to listen on the current person's profile
  *   2. Allows you to switch the current person with a search using switchTo(name, email);
  * @param ds - datasource
  * @param listener - a onValue listener which will get Person objects (which represent the current person);
  * @constructor
  */
  var PersonSpotlight = function(ds, listener) {
    var This = this;
    This._ds = ds;
    This._listener = listener;
    This._currentPersonKey = null;
    This._switchTo = {
      params: null,
      defer: null
    };

    var ignoreSource = function(target) {
      return function(source, key, obj) {
        return target(key, obj);
      };
    };

    This._people = new People({
        onChildAdded: ignoreSource(This._filterAndUpdate.bind(This)),
        onChildChanged: ignoreSource(This._filterAndUpdate.bind(This)),
        onChildRemoved: ignoreSource(This._filterAndUpdate.bind(This))
    }, {
        inviteeWriter: {
            addChild: This._ds.createInviteesChild.bind(This._ds),
            changeChild: This._ds.writeInviteeProperty.bind(This._ds),
            removeChild: This._ds.removeInviteesChild.bind(This._ds)
        },
        userProfileWriter: {
            addChild: console.log.bind(console, "userProfileWriter-add"),
            changeChild: function (userId, path, value, callback) {

                if (path.length >= 1 && path[0] == "profile") {
                    var profilePath = path.slice(1);
                    return This._ds.writeUserProfileProperty(userId, profilePath, value, callback);
                }
                return This._ds.writeUserProperty(userId, path, value, callback);
            },
            removeChild: This._ds.removeUsersChild.bind(This._ds)
        }
    });

    var inviteeListener = This._people.getInviteeListener();
    var profileListener = This._people.getProfileListener();
    var globalProfileListener = This._people.getGlobalProfilesListener();

    var setDifference = function (oldSet, newSet) {
        return {
            //elements in the new set and not in the old set = added
            addedSet: newSet.filter(function (newKey) {
                return (oldSet.indexOf(newKey) < 0);
            }),

            //elements in the old set not in the new set = removed
            removedSet: oldSet.filter(function (oldKey) {
                return (newSet.indexOf(oldKey) < 0);
            }),

            //elements in the old set also in the new set = overlap
            overlapSet: oldSet.filter(function (oldKey) {
                return (newSet.indexOf(oldKey) >= 0);
            })
        };
    };

    var oldUsersKeysWithProfiles = [];
    var oldInviteeKeys = [];
    This._ds.observeUsers(function (objs) {
        objs = objs || {};
        /**
         * If the logged in user does not have a profile, we will create a empty one for it
         */
        var userId = This._ds.getEvent().getEventsManager().getUserId();
        if (userId) {
            objs[userId] = objs[userId] || {};
            objs[userId].profile = objs[userId].profile || {};
        }

        var userKeysWithProfiles = Object
            .keys(objs);

        var setDiff = setDifference(oldUsersKeysWithProfiles, userKeysWithProfiles);
        setDiff.addedSet.forEach(function (key) {
            profileListener.onChildAdded(null, key, objs[key]);
        });
        setDiff.removedSet.forEach(function (key) {
            profileListener.onChildRemoved(null, key);
        });
        setDiff.overlapSet.forEach(function (key) {
            profileListener.onChildChanged(null, key, objs[key]);
        });
        oldUsersKeysWithProfiles = userKeysWithProfiles;

        This._checkSwitchStatus();
        This._updateListenerWithPersonAtId(This._currentPersonKey);
    });

    This._ds.observeInvitees(function (objs) {
        objs = objs || {};
        var inviteeKeys = Object
            .keys(objs);

        var setDiff = setDifference(oldInviteeKeys, inviteeKeys);

        setDiff.addedSet.forEach(function (key) {
            inviteeListener.onChildAdded(null, key, objs[key]);
        });
        setDiff.removedSet.forEach(function (key) {
            inviteeListener.onChildRemoved(null, key);
        });
        setDiff.overlapSet.forEach(function (key) {
            inviteeListener.onChildChanged(null, key, objs[key]);
        });
        oldInviteeKeys = inviteeKeys;

        This._checkSwitchStatus();
        This._updateListenerWithPersonAtId(This._currentPersonKey);
    });

    This._ds.observeServiceApiEventUserProfiles(["email"], function (profiles) {
        profiles = profiles || {};
        globalProfileListener.onValueChanged(null, profiles.users || {});
    });

    This._ds.observeRsvpStrictNameMatch((strictMode) => {
        This._strictNameMatchMode = strictMode;
    })

    //actually observes last loginless RSVPed person in cookies (not current logged in user)
    This._ds.observeCurrentPersonId(function (currentPersonId) {
        This._currentPersonKey = currentPersonId;
        This._checkSwitchStatus();
        This._updateListenerWithPersonAtId(This._currentPersonKey);
    });
  };

  PersonSpotlight.prototype._filterAndUpdate = function(key) {
    var This = this;
    if (key != This._currentPersonKey) {
      return;
    }

    This._updateListenerWithPersonAtId(This._currentPersonKey);
  };

  PersonSpotlight.prototype._updateListenerWithPersonAtId = function(personId) {
    var This = this;
    var onValue = This._listener.onValue;
    onValue(This._people.getPersonAtKey(personId));
  };

  /**
         * If we cant find the right match in this time frame we will create one
         */
  PersonSpotlight.prototype.getSwitchTimeout = function() {
    return 4000;
  };

  PersonSpotlight.prototype._switchTo_create = function(params, people) {
    var This = this;

    var nameComponents = [];
    if (params.firstName) {
      nameComponents.push((params.firstName || "").split(" ").join("\xa0"));
    }

    if (params.lastName) {
      nameComponents.push((params.lastName || "").split(" ").join("\xa0"));
    }

    var name = nameComponents.join(" ");
    var email = params.email;

    if (!name || !email) {
      throw new Error("Name and email must be present when creating a invitee");
    }

    //Todo: Use people class to create a invitee instead!
    This._ds.createInviteesChild(
      {
        name: name,
        email: email,
        createdAt: Date.now(),
        createdBy: "joy|event-permittedMember"
      },
      function(err) {
        if (err) {
          This._ds.getTelemetry().error("Error creating invitee");
          This._ds.getTelemetry().error(err);
        }
      }
    );
  };

  /**
         * Find the right person from people and write the currentPersonId
         * @param params
         * @param people
         * @returns {*}
         * @private
         */

  /**
         When strict name match is enabled, we want to be sure to
            1. check name first
            2. if name matches, check email
            3. so long as name matches (bonus points for email)
                they are on our guest list
            4. if NO match, throw exception that prompts dialog box
         **/
  PersonSpotlight.prototype._checkSwitchStatus_find = function(params, people) {
    var personKeys = people.getKeys();
    const rsvpStrictNameMatch = this._strictNameMatchMode;

    var matches = [];
    personKeys.forEach(function(personKey) {
      var person = people.getPersonAtKey(personKey);

      if (person.getDetail("wasEvicted") || person.wasEvicted) {
        return;
      }

      var score = 0;
      let name = null;
      let email = null;

      if (
        !!person.lastName &&
        !!params.lastName &&
        person.lastName.toLowerCase() == params.lastName.toLowerCase() &&
        !!person.firstName &&
        !!params.firstName &&
        person.firstName.toLowerCase() == params.firstName.toLowerCase()
      ) {
        name = true;
      }

      if (
        !!person.email &&
        !!params.email &&
        person.email.toLowerCase() == params.email.toLowerCase()
      ) {
        email = true;
      }

      if (rsvpStrictNameMatch) {
        //must match name in strictNameMatch mode
        if (name) {
          score += 50;
          //if email matches, more points assigned
          //in cases of two guests with same name this will hopefully tie break
          if (email) {
            score += 100;
          }
          matches.push({
            key: personKey,
            score,
            person: person
          });
        }
      } else if (name || email) {
        if (name) {
          score += 50;
        }
        if (email) {
          score += 100;
        }

        matches.push({
          key: personKey,
          score,
          person: person
        });
      }
    });

    var _matchesConcat = "";

    matches.forEach(function(match) {
      _matchesConcat += match.person.name + ":" + match.person.email + "; ";
    });

    This._telemetry.event({
      category: "EventDataSource",
      action: "PeopleMatchesFound",
      label: matches.length + "Found",
      value: matches.length,
      extraInfo: {
        matches: _matchesConcat
      }
    });

    if (matches.length === 0) {
      return null;
    }

    matches.sort(function(l, r) {
      //descending sort
      return r.score - l.score;
    });

    return matches[0];
  };

  PersonSpotlight.prototype._checkSwitchStatus = function() {
    var This = this;
    let rsvpStrictNameMatch = this._strictNameMatchMode;
    if (!This._switchTo.defer) {
      return;
    }

    var params = This._switchTo.params;

    var bestMatch = This._checkSwitchStatus_find(params, This._people);

    if (!bestMatch) {
      //if strict name match on, we do NOT want to create an invitee
      if (rsvpStrictNameMatch) {
        let error = new Error("strictNameMode");
        return This._switchTo.defer.reject(error);
      } else return;
    }
    This._ds
      .getTelemetry()
      .info({
        msg: "matched",
        params: This._switchTo.params,
        key: bestPersonKey
      });

    /*
            If we are here, we are done with the switch request,
                * we found a match for (This._switchTo.params)
                * and its also the current person id
            */

    var bestPersonKey = bestMatch.key;
    var bestPerson = bestMatch.person;

    var switchTo = This._switchTo;
    This._switchTo = {
      defer: null,
      params: null
    };

    /**
             * Write properties: This ensures that switching works like a search and update function.
             */
    if (params.firstName) {
      bestPerson.setFirstName(params.firstName, function() {
        if (params.lastName) {
          //First name and last name are actually writes on the same property, hence the delay.
          setTimeout(() => {
            bestPerson.setLastName(params.lastName, function() {});
          }, 200);
        }
      });
    }

    if (params.email) {
      bestPerson.setEmail(params.email, function() {});
    }

    if (This._currentPersonKey != bestPersonKey) {
      This._currentPersonKey = bestPersonKey;
      This._ds.writeCurrentPersonId(bestPersonKey, function(err) {});
    }

    switchTo.defer.resolve(bestPerson);
    This._updateListenerWithPersonAtId(This._currentPersonKey);
  };

  PersonSpotlight.prototype.getCurrentPersonKey = function() {
    var This = this;
    return This._currentPersonKey;
  };

  PersonSpotlight.prototype.getCurrentPerson = function() {
    var This = this;
    if (!This._currentPersonKey) {
      return null;
    }

    return This._people.getPersonAtKey(This._currentPersonKey);
  };

  /**
         * This class should return a promise, which is satisfied
         *   after the switch is made
         *   OR
         *   after we create a new invitee to match the request
         *
         * @param name
         * @param email.
         */
  PersonSpotlight.prototype.switchTo = function(firstName, lastName, email) {
    var This = this;
    let rsvpStrictNameMatch = this._strictNameMatchMode;
    if (This._switchTo.defer) {
      const err = new error.client.Gone(
        "This switchTo request has been replaced by a newer one"
      );
      This._switchTo.defer.reject(err);
    }

    var defer = This._ds
      .getEvent()
      .getPromiseLib()
      .defer();
    This._switchTo = {
      defer: defer,
      params: {
        firstName: firstName,
        lastName: lastName,
        email: email
      }
    };

    This._checkSwitchStatus();

    /**
             * If after the expiry time we still have not resolved the switchTo request, we create a invitee.
             * The creation of the invitee should trigger listeners causing a match to be found.
             * In the case where strictNameMatch is turned on, we want to AVOID creating a new invitee
             */

    setTimeout(function() {
      if (!This._switchTo.defer) {
        return;
      }
      if (!rsvpStrictNameMatch) {
        This._ds
          .getTelemetry()
          .info({
            msg: "no match, creating invitee",
            params: This._switchTo.params
          });
        This._switchTo_create(This._switchTo.params, This._people);
      }
    }, This.getSwitchTimeout());

    return defer.promise;
  };

  /**
         * Returns a person object for the given userId
         * @param userId
         */
  PersonSpotlight.prototype.findPersonWithUserId = function(userId) {
    if (!userId) {
      return null;
    }

    var This = this;
    var people = This._people;
    var personKeys = people.getKeys().filter(function(personKey) {
      var person = people.getPersonAtKey(personKey);
      var userProfileKeys = person.userProfileKeys || [];
      return userProfileKeys.indexOf(userId) >= 0;
    });

    if (!personKeys.length) {
      return null;
    }

    return people.getPersonAtKey(personKeys[0]);
  };

  /**
         * Assimilates the current person into the specified person thus merging their profiles.
         * @param person
         * @param callback
         */
  PersonSpotlight.prototype.assimilateCurrentPersonInto = function(
    person,
    callback
  ) {
    var This = this;

    //If current person does not exist, assimilate is a no-op
    if (!This._currentPersonKey) {
      return setImmediate(callback);
    }

    return person.assimilate(
      This._people.getPersonAtKey(This._currentPersonKey),
      function(err) {
        if (err) {
          return callback(err);
        }
        return This._ds.writeCurrentPersonId(null, callback);
      }
    );
  };

  /**
         * Clears current person
         * @param callback
         */
  PersonSpotlight.prototype.clearCurrentPersonId = function(callback) {
    var This = this;
    if (!This._currentPersonKey) {
      return setImmediate(callback);
    }
    return This._ds.writeCurrentPersonId(null, callback);
  };

  var spotlight = new PersonSpotlight(This, listener);

  return spotlight;
};

export { EventDataSource };
